import './ShippingLabelShow.scss';

import { EmailField, NumberField, RefreshButton, Show, TextField } from '@refinedev/antd';
import { useOne, useShow } from '@refinedev/core';
import { Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import PopupMenu from '@/components/general/PopupMenu';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { COMPANY_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { Country, phoneNumberMeta } from '@/utils/phoneNumbers';

const { Title } = Typography;

export const ShippingLabelShow = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const hasMenuActions = useMemo(
    () => hasPermission('edit') || hasPermission('delete'),
    [hasPermission],
  );
  const {
    result: record,
    query: { isLoading },
  } = useShow();
  const {
    result: companyResult,
    query: { isFetching: companyIsLoading },
  } = useOne({
    resource: 'empresas',
    id: record?.company || '',
    queryOptions: {
      enabled: !!record && !!record.company,
    },
  });
  const countryCode = useMemo(() => Country[record?.localidad.provincia.pais?.id], [record]);

  return (
    <Show
      isLoading={isLoading || companyIsLoading}
      headerButtons={({ refreshButtonProps, editButtonProps, deleteButtonProps }) => (
        <>
          <RefreshButton {...refreshButtonProps} hideText />
          {hasMenuActions && (
            <PopupMenu
              menuContent={
                <>
                  <ProtectedEditButton {...editButtonProps} type="text" />
                  <ProtectedDeleteButton {...deleteButtonProps} type="text" />
                </>
              }
            />
          )}
        </>
      )}>
      <div className="ShippingLabelShow">
        <div>
          <Title level={5}>{'Id'}</Title>
          <NumberField value={record?.id ?? '-'} />
        </div>
        <div>
          <Title level={5}>{t('Descripción')}</Title>
          <TextField value={record?.description} />
        </div>
        <div>
          <Title level={5}>{t('País')}</Title>
          <TextField value={record?.localidad.provincia.pais.nombre} />
        </div>
        <div>
          <Title level={5}>{t('Provincia')}</Title>
          <TextField value={record?.localidad.provincia.nombre} />
        </div>
        <div>
          <Title level={5}>{t('Localidad')}</Title>
          <TextField value={record?.localidad.nombre} />
        </div>
        <div>
          <Title level={5}>{t('Código postal')}</Title>
          <TextField value={record?.postal_code} />
        </div>
        <div>
          <Title level={5}>{t('Dirección')}</Title>
          <TextField value={record?.address} />
        </div>
        <div>
          <Title level={5}>{t('Email')}</Title>
          <EmailField value={record?.email ?? '-'} />
        </div>
        <div>
          <Title level={5}>{t('Teléfono')}</Title>
          <TextField
            value={
              record?.phone_number && countryCode
                ? `${phoneNumberMeta[countryCode].phoneCountry} ${record?.phone_number}`
                : '-'
            }
          />
        </div>
        <div>
          <Title level={5}>{t('Empresa')}</Title>
          <LinkTextField
            admin
            to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record?.company}`}
            label={companyResult?.full_name ?? record?.company}
          />
        </div>
        <div>
          <Title level={5}>{t('Contacto')}</Title>
          <TextField value={record?.contact ?? '-'} />
        </div>
      </div>
    </Show>
  );
};
